import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild } from '@angular/core';
import { AgentDto, AgentOtpChannelEnum, SentOtpDto } from '../../../../service/dto';
import { Callback } from '../../login.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../../../service/auth.service';
import { finalize } from 'rxjs';
import { NzInputOtpComponent } from 'ng-zorro-antd/input';

const RESEND_COOLDOWN_SECONDS = 60;

@Component({
	selector: 'app-login-capture-otp',
	templateUrl: './login-capture-otp.component.html',
	styleUrl: './login-capture-otp.component.scss',
	standalone: false
})
export class LoginCaptureOtpComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() agent!: AgentDto;
	@Output("doUpdateState") doUpdateState = new EventEmitter<Callback>();
	form!: FormGroup;
	submitting: boolean = false;
	showPassword = false;
	message?: string;
	sentOtp?: SentOtpDto;
	resendSecondsRemaining = RESEND_COOLDOWN_SECONDS;

	// nz-input-otp (ng-zorro-antd/input) already implements auto-advance,
	// backspace-to-previous-box and paste-fills-all-six internally
	// (NzInputOtpComponent#onInput/#onKeyDown/#onPaste) — reused as-is
	// rather than hand-rolling a six-box widget. Its template has no
	// input for extra attributes on the generated <input>s though, so
	// inputmode/autocomplete (kenya-restyle.md §3b, for the mobile
	// SMS-code autofill prompt) are set directly on its view children.
	@ViewChild('otpRef') otpRef?: NzInputOtpComponent;
	private resendTimer?: any;

	constructor(private authService: AuthService, private fb: FormBuilder) {

	}

	ngOnInit(): void {
		this.form = this.fb.group({
			otp: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
		});
		this.authService.doSendLoginOtp(this.agent.username, true).subscribe(result => {
			this.sentOtp = result;
		});
		this.startResendCountdown();
	}

	ngAfterViewInit(): void {
		this.applyOtpInputAttributes();
	}

	ngOnDestroy(): void {
		if (this.resendTimer) {
			clearInterval(this.resendTimer);
		}
	}

	private applyOtpInputAttributes(): void {
		const inputs: QueryList<ElementRef<HTMLInputElement>> | undefined = this.otpRef?.otpInputs;
		inputs?.forEach(input => {
			input.nativeElement.setAttribute('inputmode', 'numeric');
			input.nativeElement.setAttribute('autocomplete', 'one-time-code');
			input.nativeElement.placeholder = '·';
		});
	}

	private startResendCountdown(): void {
		this.resendSecondsRemaining = RESEND_COOLDOWN_SECONDS;
		if (this.resendTimer) {
			clearInterval(this.resendTimer);
		}
		this.resendTimer = setInterval(() => {
			this.resendSecondsRemaining = Math.max(0, this.resendSecondsRemaining - 1);
			if (this.resendSecondsRemaining === 0) {
				clearInterval(this.resendTimer);
			}
		}, 1000);
	}

	get resendCountdownLabel(): string {
		const minutes = Math.floor(this.resendSecondsRemaining / 60);
		const seconds = this.resendSecondsRemaining % 60;
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	get channelTileLabel(): string {
		switch (this.sentOtp?.sentViaChannel) {
			case 'Email': return 'EMAIL';
			case 'AuthenticatorApp': return 'APP';
			default: return 'SMS';
		}
	}

	onSubmit() {
		const otp = this.form.get('otp')!.value;

		this.submitting = true;
		this.authService.doAuthenticate(this.agent.username, this.agent.password!, otp)
			.pipe(
				finalize(() => this.submitting = false)
			)
			.subscribe(result => {
				if (result.success == false) {
					this.message = result.message;
					// Never clear the boxes on a wrong code (kenya-restyle.md
					// §3b) — the control is left exactly as the person typed
					// it; only focus moves back to the first box.
					this.otpRef?.otpInputs?.first?.nativeElement?.focus();
					this.otpRef?.otpInputs?.first?.nativeElement?.select();
					return;
				}
				switch (result.agent!.status) {
					case 'PendingRegistration': return this.doUpdateState.emit({ mode: 'CaptureRegistrationOtp', agent: result.agent });
					case 'PendingApproval': return this.doUpdateState.emit({ mode: 'PendingApproval', agent: result.agent });
					case 'Rejected': return this.doUpdateState.emit({ mode: 'Rejected', agent: result.agent });
					case 'Approved': return this.doUpdateState.emit({ mode: 'CapturePassword', agent: result.agent });
					case 'Blocked': return this.doUpdateState.emit({ mode: 'Blocked', agent: result.agent });
					case 'Suspended': return this.doUpdateState.emit({ mode: 'Suspended', agent: result.agent });
				}
			});
	}

	reset() {
		this.doUpdateState.emit({ mode: 'CaptureUsername', agent: this.agent });
	}
	isInvalid(field: string): boolean {
		return !!(this.form.get(field)?.invalid && this.form.get(field)?.touched);
	}
	// Defaults to the channel already in use so the plain "Resend" link
	// (kenya-restyle.md §3b's countdown control) re-sends the same way
	// without the person re-picking a channel; the per-channel links
	// below it still pass an explicit channel to switch.
	reSend(channel?: AgentOtpChannelEnum){
		if (this.resendSecondsRemaining > 0) {
			return;
		}
		const targetChannel = channel ?? this.sentOtp?.sentViaChannel;
		this.authService.doSendLoginOtp(this.agent.username, false, targetChannel).subscribe(result => {
			this.sentOtp = result;
			this.startResendCountdown();
		});
	}
}
